import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useFirebase } from './FirebaseContext';

const FavoritesContext = createContext();

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { db, auth } = useFirebase();

  // Load favorites when user changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        loadFavorites();
      } else {
        setFavorites([]);
        setError(null);
      }
    });

    return unsubscribe;
  }, [auth]);

  const loadFavorites = async () => {
    const user = auth.currentUser;
    if (!user) {
      // Guest users have no favorites - clear and return silently
      setFavorites([]);
      setError(null);
      return;
    }

    console.log('Loading favorites for user:', user.uid);
    console.log('User email:', user.email);

    setLoading(true);
    setError(null);

    try {
      // Get favorite menuItemIds from favorites collection (like iOS app)
      const favoritesRef = collection(db, 'users', user.uid, 'favorites');
      console.log('Querying favorites path:', `users/${user.uid}/favorites`);
      
      const snapshot = await getDocs(favoritesRef);
      console.log('Favorites snapshot size:', snapshot.size);
      
      const menuItemIds = snapshot.docs.map(doc => doc.id);
      console.log('Menu item IDs from favorites:', menuItemIds);
      
      if (menuItemIds.length === 0) {
        setFavorites([]);
        return;
      }

      // Fetch restaurants first (like iOS app)
      const restaurantsRef = collection(db, 'restaurants');
      const restaurantsSnapshot = await getDocs(
        query(restaurantsRef, where('isAcceptingOrders', '==', true))
      );
      
      console.log('Accepting restaurants count:', restaurantsSnapshot.size);
      
      // Fetch menu items from each restaurant's subcollection (like iOS app)
      const allMenuItems = [];
      
      for (const restaurantDoc of restaurantsSnapshot.docs) {
        const restaurantId = restaurantDoc.id;
        const restaurantData = restaurantDoc.data();
        
        console.log(`Fetching menu items for restaurant: ${restaurantId}`);
        
        const menuItemsRef = collection(db, 'restaurants', restaurantId, 'menuItems');
        const menuItemsSnapshot = await getDocs(menuItemsRef);
        
        const restaurantMenuItems = menuItemsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || 'Unknown Item',
            description: data.description || '',
            price: data.price || 0,
            category: data.category || 'Food',
            imageURL: data.menuItemImageURL || data.imageURL || '',
            restaurantId: restaurantId,
            restaurantName: restaurantData.name || 'Restaurant',
            restaurantType: restaurantData.restaurantType || 'Restaurant',
            island: restaurantData.island || 'St. Kitts',
            parish: restaurantData.parish || 'Saint George Basseterre'
          };
        });
        
        allMenuItems.push(...restaurantMenuItems);
        console.log(`Found ${restaurantMenuItems.length} menu items for restaurant ${restaurantId}`);
      }
      
      console.log('Total menu items available:', allMenuItems.length);
      
      // Filter menu items to get only favorites (like iOS app)
      const favoriteItems = allMenuItems
        .filter(menuItem => menuItemIds.includes(menuItem.id))
        .map(item => ({
          ...item,
          addedAt: snapshot.docs.find(doc => doc.id === item.id)?.data()?.addedAt
        }));

      console.log('Processed favorites (like iOS):', favoriteItems);
      setFavorites(favoriteItems);
    } catch (err) {
      setError(err.message);
      console.error('Error loading favorites:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (menuItem) => {
    const user = auth.currentUser;
    if (!user) {
      // Guest users can't toggle favorites - return false to indicate failure
      // The UI should handle showing login prompt
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Use menuItem.id as the document ID (like iOS app)
      const favoriteRef = doc(db, 'users', user.uid, 'favorites', menuItem.id);
      const isCurrentlyFavorite = favorites.some(item => item.id === menuItem.id);

      if (isCurrentlyFavorite) {
        // Remove from favorites
        await deleteDoc(favoriteRef);
        setFavorites(prev => prev.filter(item => item.id !== menuItem.id));
      } else {
        // Add to favorites with minimal data (like iOS app)
        await setDoc(favoriteRef, {
          menuItemName: menuItem.name,
          addedAt: serverTimestamp()
        });
        
        // Reload favorites to get complete data
        await loadFavorites();
      }
    } catch (err) {
      setError(err.message);
      console.error('Error toggling favorite:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFavorite = (menuItemId) => {
    return favorites.some(item => item.id === menuItemId);
  };

  const value = {
    favorites,
    loading,
    error,
    loadFavorites,
    toggleFavorite,
    isFavorite
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
